import { ImageCollectionItemT, MappingClientT, ObjectContainerClientT, PolygonT, StatusClientT } from 'bovaljaren-models';
import React, { ReactElement, useEffect, useState } from 'react';
import { SubContent } from '../../Wrappers';
import { getObjectClassNameFromStatus } from '../mapping/SvgMapping/SvgMapping';
import './PolygonPreview.scss';

type Props={
    object: ObjectContainerClientT;
    mapping?: MappingClientT;
    image: ImageCollectionItemT;
    statuses?: StatusClientT[];
    floorplan?: boolean;
};

type PolygonMeasurementsT = {
    xMin: number;
    xMid: number;
    xMax: number;
    yMin: number;
    yMid: number;
    yMax: number;
}

export const polygonMeasurements = (polycords: PolygonT): PolygonMeasurementsT => {

    const xValues = polycords.map(coords => coords[0]);
    const yValues = polycords.map(coords => coords[1]);

    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const xMid = (xMax - ((xMax - xMin) / 2));
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);
    const yMid = (yMax - ((yMax - yMin) / 2));

    return { xMin, xMid, xMax, yMin, yMid, yMax };

};

const PolygonPreview: React.FC<Props> = ({ children, mapping, object, image, statuses, floorplan }): ReactElement => {
    const [measure, setMeasure] = useState<PolygonMeasurementsT>();

    useEffect(() => {
        if (!mapping) return;

        setMeasure(polygonMeasurements(mapping.polycords));
    }, [mapping]);

    const getStyle = (measure: PolygonMeasurementsT): { transform: string } => {
        //Calculates the length of the Polygons diagonal, for a unified scaling with regards to the polygon.
        const diagonal = Math.sqrt(Math.pow((measure.xMax - measure.xMin), 2) + Math.pow((measure.yMax - measure.yMin), 2));

        const scale = 2 - (diagonal / 100); // max scale = 2;
        const bounds = ((scale - 1) * 50) / scale ;
        const translate = { x: 50 - measure.xMid, y: 50 - measure.yMid };

        translate.x = Math.min(Math.max(translate.x, -bounds), bounds);
        translate.y = Math.min(Math.max(translate.y, -bounds), bounds);

        return { transform:`scale(${scale}) translate(${translate.x}%, ${-5 + translate.y}%)` };
    };

    return (
        <div className={'bv-object-polygon-preview'} style={{ flex: '0 0 auto', width: '100%', height: '30%', overflow: 'hidden' }}>
            {measure && <div
                className={'bv-nospace bv-content'}
                style={{ ...getStyle(measure) }}>
                <img src={image.url?.medium} className={'bv-pp-image'} alt=''/>
                <SubContent>
                    <svg
                        className={'bv-pp-svg'}
                        version="1.1"
                        viewBox={'0 0 100 100'}
                        preserveAspectRatio='none'
                        xmlns="http://www.w3.org/2000/svg">
                        <polygon
                            className={(object?.statusId && statuses && `bv-pp-polygon ${getObjectClassNameFromStatus(statuses, object.statusId)}`) || 'bv-pp-polygon bv-available'}
                            vectorEffect={'non-scaling-stroke'}
                            points={mapping?.polycords.join(',')}/>
                    </svg>
                </SubContent>
            </div>}
            {children}
        </div>
    );
};


export default PolygonPreview;